import { ImageTags } from '@/constants/CompressorImageTagData.const';
import { DeviceKind } from '@/constants/enums';

export const COMPRESSOR_DATA = 'compressorData';
export const ROOMS = 'rooms';
export const IMAGES = 'images';
export const NO_MATCHING_SPEC = 'no-matching-spec';

export enum PressureType {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

export enum AuthenticationType {
  SSO = 'sso',
  PWD = 'password',
}

export enum PressureTypeFull {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  MAIN = 'main',
}

export enum TanksType {
  WET = 'wet',
  DRY = 'dry',
}

export enum DryerType {
  DESICCANT = 'Desiccant',
  REFRIGERATED = 'Refrigerated',
}

export enum TypeOptions {
  FS = 'FS',
  VSD = 'VSD',
  CENTRIFUGAL = 'Centrifugal',
}

export enum PlatformOptions {
  CENTRIFUGAL = 'Centrifugal',
  OIL_FLOODED = 'Oil flooded',
  TWO_STAGE_DRY_SCREW = '2 Stage Dry Screw',
  RECIPROCATING = 'Reciprocating',
}

export enum CoolingOptions {
  AIR = 'Air',
  WATER = 'Water',
}

export interface OnboardingFormContactPerson {
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface OnboardingFormSiteAddress {
  streetAddress: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  cityState?: string;
}

export type ImageData = {
  id?: number;
  tag: ImageTags;
  url?: string;
  originalName?: string;
  encodedS3Key?: string;
};

export interface CompressorData {
  number: number;
  serialNumber: string;
  model: string;
  modelSpec: string;
  controller: string;
  images?: ImageData[];
  customSpecFlow?: number;
  customSpecPower?: number;
  customSpecPressure?: number;
  isManual?: boolean;
  additionalInformation?: AdditionalInformationData;
}

export interface AdditionalInformationData {
  specUrl?: string;
  platform?: string;
  pressure?: number;
  driveType?: string;
  cooling?: string;
  horsepower?: number;
}

export interface Tank {
  tankType: TanksType;
  tankValue: number;
}

export interface Dryer {
  dryerType: DryerType;
}

export interface Room {
  compressorsInRoom: string[];
  compressorData: CompressorData[];
  tanks: Tank[];
  dryers: Dryer[];
  newTank?: Tank;
  newDryer?: Dryer;
}

export interface OnboardingFormGeneralData {
  siteAddress?: OnboardingFormSiteAddress;
  contactPerson?: OnboardingFormContactPerson;
  accountManager?: OnboardingFormContactPerson;
  stationTier?: string;
  stationUsageType?: string;
  ecoplantCustomerId?: string;
  customerName?: string;
  channelIdentifier?: string;
}

export interface SiteGeneralConfiguration {
  numberCompressorRooms?: number;
  numberEntryPointsMainHeader?: number;
  systemControllerType?: string;
  stationNameSuffix?: string;
  isManyTargetPressure?: boolean;
  pressureType?: PressureTypeFull;
  roomsData?: Room[];
}

export interface OnboardingSiteData {
  generalData: OnboardingFormGeneralData;
  siteGeneralConfiguration?: SiteGeneralConfiguration;
}

export class ImageUploadSummary {
  failedUploads: string[];
  totalImages: number;
}

export interface OnboardingFormPayload {
  salesforceAccountId: string;
  formId?: string;
  siteData: OnboardingSiteData;
  imageUploadSummary?: ImageUploadSummary;
}

export interface CustomerInformationStepData {
  tier: string;
  stationUsageType: string;
  account: string;
  customer: CustomerData;
  newCustomerName: string;
  siteAddress: SiteAddressData;
  contactPerson: ContactPersonData;
  accountManager: ContactPersonData;
}

export interface SiteConfigurationStepData {
  compressorRooms: number | 'other';
  entryPoints: number | 'other';
  systemControllerType: string;
  stationNameSuffix: string;
  hasMultipleTargetPressures: boolean;
  pressureType: string;
  // roomsData removed: room data is now handled in step 3 and added directly to the payload
  otherCompressorRooms?: number;
  otherEntryPoints?: number;
  pressureTypes?: string[];
  rooms?: PressureTypeRoom[];
}

export interface PressureTypeRoomData {
  rooms: PressureTypeRoom[];
}

export interface PressureTypeRoom {
  compressorsInRoom?: string[];
  compressorData?: CompressorData[];
  tanks?: Tank[];
  dryers?: Dryer[];
  newTank?: Tank;
  newDryer?: Dryer;
}

export interface RoomConfigurationStepData {
  assets: Asset[];
  rooms?: PressureTypeRoom[];
  [pressureType: string]: Asset[] | PressureTypeRoomData | PressureTypeRoom[] | undefined;
}

export type PressureTypeData<T> = T extends PressureTypeRoomData ? T : never;

export interface StoreErrors {
  fetch?: string;
  [key: string]: string | undefined;
}

export interface UserData {
  customerName: string;
  customerId: string;
}

export interface ModelSpec {
  modelSpecName: string;
  modelDisplayName: string;
  modelId: string;
  deviceControllerId: string;
  deviceControllerName: string;
  id: string;
  technicalName: string;
  deviceKind: DeviceKind;
}

export interface Asset {
  id: string;
  name: string;
  serialNumber: string;
  model: string;
  status: string;
  modelNameCpqC?: string;
  localControllerTypeC?: string;
}

export interface SiteAddress {
  city: string;
  state: string;
  country: string;
  street: string;
  postalCode: string;
}

export interface AccountResponse {
  formId?: string;
  accountId: string;
  customerName: string;
  accountOwnerC: string;
  siteAddress: SiteAddress;
  cgOrgChannelC: string;
  assets: Asset[];
  siteData?: OnboardingSiteData;
  status?: FormStatus;
}

export interface CustomerData {
  name: string;
  accountId: string;
  cgOrgChannelC: string;
}

export interface SiteAddressData {
  streetAddress: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
}

export interface ContactPersonData {
  fullName: string;
  email: string;
}

// Tier constants
export const TIER_VALUES = {
  MONITOR: 'monitor',
  OPTIMIZE: 'optimize',
  MONITOR_INTERNAL: 'monitor-internal',
} as const;

// Station Usage Type constants
export const STATION_USAGE_TYPES = {
  REGULAR: 'Regular',
  INTERNAL: 'Internal',
} as const;

// Station Name Suffix constants
export const STATION_NAME_SUFFIXES = {
  MAIN: 'Main',
  INTERNAL: 'Internal',
} as const;

export const TIER_OPTIONS = [
  { value: TIER_VALUES.MONITOR, label: 'Monitor' },
  { value: TIER_VALUES.OPTIMIZE, label: 'Optimize' },
  { value: TIER_VALUES.MONITOR_INTERNAL, label: 'Monitor (Internal)' },
];

export const NUMBER_OPTIONS = [1, 2, 3, 4, 5];

export const DEFAULT_SITE_CONFIGURATION_STEP_VALUES = {
  compressorRooms: 1,
  entryPoints: 1,
  systemControllerType: '',
  stationNameSuffix: '',
  hasMultipleTargetPressures: false,
  pressureType: PressureTypeFull.MAIN,
  roomsData: [],
  otherCompressorRooms: undefined,
  otherEntryPoints: undefined,
};

export const DEFAULT_CUSTOMER_INFORMATION_STEP_VALUES: CustomerInformationStepData = {
  tier: '',
  stationUsageType: STATION_USAGE_TYPES.REGULAR,
  account: '',
  newCustomerName: '',
  customer: {
    name: '',
    accountId: '',
    cgOrgChannelC: '',
  },
  siteAddress: {
    streetAddress: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
  },
  contactPerson: {
    fullName: '',
    email: '',
  },
  accountManager: {
    fullName: '',
    email: '',
  },
};

export const DEFAULT_ROOM_CONFIGURATION_STEP_VALUES: RoomConfigurationStepData = {
  assets: [],
  rooms: [],
};

export enum FormStatus {
  DESIGN = 'design',
  PENDING = 'pending',
  IN_PROGRESS = 'in progress',
  FAIL = 'fail',
  COMPLETE = 'complete',
}

export interface FormStatusResponse {
  status: FormStatus;
  stationId?: string;
  createdEntitiesData?: CreatedEntitesData;
  imageUploadSummary?: ImageUploadSummary;
}

export interface CreatedEntitesData {
  customerId?: string;
  siteId?: string;
  stationId?: string;
}

export interface FormValues {
  accountId?: string;
  tier?: string;
  account?: string;
  customer?: CustomerData;
  siteAddress?: SiteAddressData;
  contactPerson?: ContactPersonData;
  compressorRooms?: number | 'other';
  entryPoints?: number | 'other';
  systemControllerType?: string;
  stationNameSuffix?: string;
  hasMultipleTargetPressures?: boolean;
  pressureType?: string;
  otherCompressorRooms?: number;
  otherEntryPoints?: number;
  pressureTypes?: string[];
  rooms?: PressureTypeRoom[];
  assets?: Asset[];
  low?: PressureTypeRoomData;
  medium?: PressureTypeRoomData;
  high?: PressureTypeRoomData;
}

export interface TankFormType {
  tankType: TanksType;
  tankValue: number | null;
}

export interface DryerFormType {
  dryerType: DryerType;
}

export type CreateImagePayload = {
  id: string;
  count: number;
  serial: string;
  tag: ImageTags;
  file: File;
};

export type SaveCompressorDataPayload = Omit<CompressorData, 'images'>;

export type SaveRoomPayload = Omit<Room, 'compressorData'> & {
  compressorData: SaveCompressorDataPayload[];
};

export type SaveSiteGeneralConfigurationPayload = Omit<SiteGeneralConfiguration, 'roomsData'> & {
  roomsData?: SaveRoomPayload[];
};

export type SaveOnboardingSiteDataPayload = Omit<OnboardingSiteData, 'dsad'> & {
  siteGeneralConfiguration?: SaveSiteGeneralConfigurationPayload;
};

export type SaveOnboardingFormPayload = {
  salesforceAccountId: string;
  formId?: string;
  region?: string;
  siteData: SaveOnboardingSiteDataPayload;
  images?: CreateImagePayload[];
};
